import { FamilyMemberHistory } from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart } from "@metriport/fhir-sdk";

export type FamilyMemberHistoryRowData = {
  id: string;
  familyMember: string;
  sex: string;
  conditions: string;
  deceased: string;
  originalData: Smart<FamilyMemberHistory>;
};

export function getFamilyMemberHistoryRows({
  bundle,
}: {
  bundle: FhirBundleSdk;
}): FamilyMemberHistoryRowData[] {
  try {
    const familyMemberHistories = bundle.getFamilyMemberHistories();
    return familyMemberHistories.map(fmh => {
      // Extract family member name from relationship
      const familyMemberName = getFamilyMemberDisplay(fmh);

      // Extract sex
      const sex = typeof fmh.sex === "string" ? fmh.sex : fmh.sex?.coding?.[0]?.display || "-";

      // Extract conditions from condition field
      const conditions =
        fmh.condition?.map(c => c.code?.coding?.[0]?.display || c.code?.text || "-").join(", ") ||
        "-";

      // Extract deceased status with comprehensive logic
      const deceasedInfo = renderFamilyMemberDeceased(fmh);

      return {
        id: fmh.id || "-",
        familyMember: familyMemberName,
        sex,
        conditions,
        deceased: deceasedInfo,
        originalData: fmh,
      };
    });
  } catch (error) {
    console.warn("FamilyMemberHistory not available in FhirBundleSdk:", error);
    return [];
  }
}

function getFamilyMemberDisplay(familyMemberHistory: FamilyMemberHistory): string {
  const codings = familyMemberHistory.relationship?.coding || [];
  const displays = codings.map(coding => coding.display).filter(Boolean);

  if (displays.length) {
    return displays.join(", ");
  } else if (familyMemberHistory.relationship?.text) {
    return familyMemberHistory.relationship.text;
  }

  return "-";
}

function asYesNo(value: boolean): "yes" | "no" {
  return value ? ("yes" as const) : ("no" as const);
}

export const renderFamilyMemberDeceased = (
  familyMemberHistory: FamilyMemberHistory
): "yes" | "no" | string => {
  const deceasedBoolean = familyMemberHistory.deceasedBoolean;
  if (deceasedBoolean !== undefined) {
    return asYesNo(deceasedBoolean);
  }

  const conditionContributedToDeath = familyMemberHistory.condition?.find(
    condition => condition.contributedToDeath
  );

  if (conditionContributedToDeath?.contributedToDeath !== undefined) {
    return asYesNo(conditionContributedToDeath.contributedToDeath);
  }

  // Handle deceasedAge and deceasedString
  if (familyMemberHistory.deceasedAge) {
    return `${familyMemberHistory.deceasedAge.value} ${familyMemberHistory.deceasedAge.code}`;
  }

  if (familyMemberHistory.deceasedString) {
    return familyMemberHistory.deceasedString;
  }

  return "-";
};
